import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "../_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Truck, ArrowDownToLine, ArrowUpFromLine, ArrowDownUp, Plus, Scale, FileDown, CheckCircle2, Clock, Recycle, ChevronRight, Search,
} from "lucide-react";
import { toast } from "sonner";

const money2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const unitLabel = (u) => (u === "m3" ? "m³" : u === "load" ? "loads" : "t");
const CHARGE_METHODS = [
  { v: "FREE", l: "Free" }, { v: "PER_TONNE", l: "Per tonne" }, { v: "PER_M3", l: "Per m³" },
  { v: "PER_LOAD", l: "Per load" }, { v: "FIXED", l: "Fixed price" },
];
const VISIT_TYPES = [
  { v: "TIP", l: "Tip material", icon: ArrowDownToLine, d: "Truck arrives loaded and tips material into the yard." },
  { v: "COLLECT", l: "Collect material", icon: ArrowUpFromLine, d: "Truck arrives empty to collect product." },
  { v: "TIP_AND_COLLECT", l: "Tip + Collect", icon: ArrowDownUp, d: "Truck tips material, then collects product — one visit." },
];
const STATUS_COLOR = {
  ARRIVED: "bg-blue-500/15 text-blue-600", AT_WEIGHBRIDGE: "bg-indigo-500/15 text-indigo-600",
  WAITING_TO_TIP: "bg-amber-500/15 text-amber-600", TIPPING: "bg-orange-500/15 text-orange-600",
  WAITING_FOR_LOADER: "bg-purple-500/15 text-purple-600", LOADING: "bg-orange-500/15 text-orange-600",
  FINAL_WEIGH: "bg-indigo-500/15 text-indigo-600", READY_TO_LEAVE: "bg-emerald-500/15 text-emerald-600",
  COMPLETED: "bg-zinc-500/15 text-zinc-500", CANCELLED: "bg-red-500/15 text-red-500",
};
const chip = (s) => `px-2 py-0.5 rounded-full text-[10px] uppercase font-bold ${STATUS_COLOR[s] || "bg-zinc-500/15 text-zinc-500"}`;

function useYardRefs(open) {
  const [refs, setRefs] = useState({ clients: [], materials: [], products: [], stockpiles: [], settings: {} });
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/clients").then((r) => r.data).catch(() => []),
      api.get("/sy/materials").then((r) => r.data.materials).catch(() => []),
      api.get("/sy/products").then((r) => r.data.products).catch(() => []),
      api.get("/sy/stockpiles").then((r) => r.data.stockpiles).catch(() => []),
      api.get("/sy/settings").then((r) => r.data).catch(() => ({})),
    ]).then(([clients, materials, products, stockpiles, settings]) =>
      setRefs({ clients: clients || [], materials, products, stockpiles, settings }));
  }, [open]);
  return refs;
}

function NewVisitDialog({ open, onOpenChange, onCreated, initialType }) {
  const refs = useYardRefs(open);
  const [f, setF] = useState({});
  const [matches, setMatches] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [searching, setSearching] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (open) { setF({ visit_type: initialType || "", save_truck: true }); setMatches([]); setSelectedTruck(null); } }, [open, initialType]);

  useEffect(() => {
    if (selectedTruck) return;
    const r = (f.truck_rego || "").trim();
    if (r.length < 2) { setMatches([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/sy/trucks?q=${encodeURIComponent(r)}`).then((res) => setMatches(res.data.trucks || [])).catch(() => setMatches([])).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [f.truck_rego, selectedTruck]);

  const chooseTruck = (t) => {
    setSelectedTruck(t); setMatches([]);
    setF((s) => ({ ...s, truck_rego: t.registration, truck_id: t.id,
      customer_id: t.customer_id || s.customer_id, customer_name: t.customer_name || s.customer_name,
      truck_type: t.truck_type || s.truck_type, driver: t.driver || s.driver, save_truck: true }));
  };
  const clearTruck = () => { setSelectedTruck(null); setF((s) => ({ ...s, truck_id: null })); };
  const onCustomer = (val) => {
    const m = refs.clients.find((c) => (c.name || "").toLowerCase() === val.toLowerCase());
    setF((s) => ({ ...s, customer_name: val, customer_id: m ? m.id : null }));
  };

  const rego = (f.truck_rego || "").trim();
  const isUnknown = !selectedTruck && rego.length >= 2 && !searching && matches.length === 0;
  const types = (refs.settings?.truck_types || []).filter((t) => t && t.name);

  const submit = async () => {
    if (!f.visit_type) return toast.error("Choose what they're here for");
    if (!rego) return toast.error("Truck rego is required");
    if (!f.customer_name) return toast.error("Customer is required");
    if (!f.truck_type) return toast.error("Truck type is required");
    try {
      const { data } = await api.post("/sy/visits", {
        visit_type: f.visit_type, customer_name: f.customer_name, customer_id: f.customer_id || null,
        truck_id: selectedTruck ? selectedTruck.id : null, truck_rego: rego, truck_type: f.truck_type,
        driver: f.driver, save_truck: selectedTruck ? true : !!f.save_truck,
      });
      toast.success(`${data.visit_number} checked in`);
      onOpenChange(false); onCreated?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not check in truck"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] max-h-[92vh] overflow-y-auto" data-testid="new-visit-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-display text-2xl">New yard visit</DialogTitle>
          <DialogDescription>Check a truck into the yard.</DialogDescription>
        </DialogHeader>

        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">What are they here for?</div>
        <div className="grid grid-cols-3 gap-2.5">
          {VISIT_TYPES.map((t) => (
            <button key={t.v} onClick={() => set("visit_type", t.v)} className={`rounded-xl border p-3 flex flex-col items-center gap-1.5 text-center transition-colors ${f.visit_type === t.v ? "border-[#FF5F15] bg-[#FF5F15]/10" : "border-zinc-200 dark:border-white/10 hover:border-[#FF5F15]/50"}`} data-testid={`visit-type-${t.v}`}>
              <t.icon className={`w-6 h-6 ${f.visit_type === t.v ? "text-[#FF5F15]" : "text-zinc-500"}`} />
              <span className="text-xs font-bold leading-tight">{t.l}</span>
              <span className="text-[10px] text-zinc-400 leading-tight hidden sm:block">{t.d}</span>
            </button>
          ))}
        </div>

        <div className="mt-1">
          <Label className="text-xs uppercase font-bold">Truck rego *</Label>
          <div className="relative">
            <Truck className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={f.truck_rego || ""} onChange={(e) => { clearTruck(); set("truck_rego", e.target.value.toUpperCase()); }} placeholder="Search or enter registration…" className="pl-9 h-11 text-lg font-mono tracking-wide" data-testid="visit-truck" />
          </div>
          {!selectedTruck && matches.length > 0 && (
            <div className="mt-1 border border-zinc-200 dark:border-white/10 rounded-lg overflow-hidden" data-testid="truck-matches">
              {matches.slice(0, 5).map((m) => (
                <button key={m.id} onClick={() => chooseTruck(m)} className="w-full text-left px-3 py-2 hover:bg-zinc-50 dark:hover:bg-white/5 flex items-center justify-between border-b border-zinc-100 dark:border-white/5 last:border-0" data-testid={`truck-match-${m.registration}`}>
                  <div>
                    <div className="font-bold font-mono">{m.registration}</div>
                    <div className="text-xs text-zinc-500">{m.customer_name || "—"}{m.truck_type ? ` · ${m.truck_type}` : ""}</div>
                  </div>
                  <div className="text-right text-[11px] text-zinc-400">{m.latest_known_tare != null ? <>Tare {m.latest_known_tare}t<br />{m.latest_tare_date || ""}</> : "New tare"}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedTruck && (
          <div className="rounded-lg bg-[#FF5F15]/5 border border-[#FF5F15]/20 p-3 flex items-start justify-between gap-2" data-testid="returning-truck">
            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-wider text-[#FF5F15] font-bold">Returning truck</div>
              <div className="font-bold font-mono">{selectedTruck.registration} <span className="font-sans font-normal text-zinc-500">· {selectedTruck.truck_type || "—"}</span></div>
              <div className="text-zinc-500">{selectedTruck.customer_name || "—"}</div>
              {selectedTruck.latest_known_tare != null && <div className="text-[11px] text-zinc-400 mt-0.5">Last known tare <b>{selectedTruck.latest_known_tare}t</b>{selectedTruck.latest_tare_date ? ` · ${selectedTruck.latest_tare_date}` : ""} <i>(informational)</i></div>}
            </div>
            <button onClick={clearTruck} className="text-xs text-zinc-400 hover:text-[#FF5F15] uppercase font-bold" data-testid="change-truck">Change</button>
          </div>
        )}
        {isUnknown && (
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/25 p-2.5 text-sm" data-testid="new-truck-note">
            <span className="font-bold text-amber-700 dark:text-amber-500">New truck.</span> <span className="text-zinc-600 dark:text-zinc-300">{rego} isn't known to this yard yet.</span>
          </div>
        )}

        <div>
          <Label className="text-xs uppercase font-bold">Customer *</Label>
          <Input list="yv-clients" value={f.customer_name || ""} onChange={(e) => onCustomer(e.target.value)} placeholder="Search customer" data-testid="visit-customer" />
          <datalist id="yv-clients">{refs.clients.map((c) => <option key={c.id} value={c.name} />)}</datalist>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase font-bold">Truck type *</Label>
            <Select value={f.truck_type || ""} onValueChange={(v) => set("truck_type", v)}>
              <SelectTrigger data-testid="visit-trucktype"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(f.truck_type && !types.some((t) => t.name === f.truck_type) ? [{ name: f.truck_type }, ...types] : types).map((t) => <SelectItem key={t.name} value={t.name}>{t.name}{t.estimated_capacity ? ` (~${t.estimated_capacity}${unitLabel(t.unit)})` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase font-bold">Driver</Label><Input value={f.driver || ""} onChange={(e) => set("driver", e.target.value)} placeholder="Optional" data-testid="visit-driver" /></div>
        </div>
        {!selectedTruck && rego.length >= 2 && (
          <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300"><input type="checkbox" checked={!!f.save_truck} onChange={(e) => set("save_truck", e.target.checked)} data-testid="save-truck" /> Save this truck for future visits</label>
        )}

        <DialogFooter className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="uppercase text-xs font-bold tracking-wider" data-testid="visit-cancel">Cancel</Button>
          <Button onClick={submit} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="visit-create">Check in truck →</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VisitDetail({ visitId, open, onOpenChange, onChanged }) {
  const refs = useYardRefs(open);
  const [v, setV] = useState(null);
  const [wb, setWb] = useState({});
  const [inb, setInb] = useState({ unit: "t", measurement_source: "WEIGHBRIDGE", charge_method: "PER_TONNE" });
  const [col, setCol] = useState({ unit: "t" });
  const load = useCallback(() => { if (visitId) api.get(`/sy/visits/${visitId}`).then((r) => setV(r.data)).catch(() => {}); }, [visitId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!v) return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent /></Dialog>;

  const isTip = v.visit_type === "TIP" || v.visit_type === "TIP_AND_COLLECT";
  const isCol = v.visit_type === "COLLECT" || v.visit_type === "TIP_AND_COLLECT";
  const weights = v.weights || {};
  const done = v.status === "COMPLETED";
  const refresh = () => { load(); onChanged?.(); };

  const weigh = async (stage, method = "MEASURED") => {
    const w = wb[stage];
    if (!w) return toast.error("Enter a weight");
    try { await api.patch(`/sy/visits/${v.id}/weigh`, { stage, weight: Number(w), tare_method: method }); toast.success("Weight captured"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const confirmInbound = async () => {
    if (!inb.material_id || !inb.stockpile_id) return toast.error("Material and stockpile required");
    if (inb.measurement_source !== "WEIGHBRIDGE" && !inb.quantity) return toast.error("Enter a quantity");
    try { await api.post(`/sy/visits/${v.id}/confirm-inbound`, { ...inb, quantity: inb.quantity ? Number(inb.quantity) : null, rate: Number(inb.rate || 0) }); toast.success("Inbound confirmed"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const requestCollect = async () => {
    if (!col.product_id || !col.stockpile_id || !col.target_quantity) return toast.error("Product, stockpile and quantity required");
    try { await api.post(`/sy/visits/${v.id}/request-collect`, { ...col, target_quantity: Number(col.target_quantity), price_override: col.price_override ? Number(col.price_override) : null }); toast.success("Sent to loader queue"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const complete = async () => {
    try { await api.post(`/sy/visits/${v.id}/complete`); toast.success("Visit completed"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openPdf = async () => {
    try { const res = await api.get(`/sy/visits/${v.id}/pdf`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Could not open PDF"); }
  };
  const openDocket = async (loadId) => {
    try { const res = await api.get(`/sy/dockets/${loadId}/pdf`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Could not open movement docket"); }
  };

  const estNet = weights.inbound_gross && v.latest_known_tare ? (weights.inbound_gross.weight - v.latest_known_tare).toFixed(2) : null;
  const measuredNet = weights.inbound_gross && weights.inbound_tare ? (weights.inbound_gross.weight - weights.inbound_tare.weight).toFixed(2) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="visit-detail">
        <DialogHeader>
          <DialogTitle className="uppercase font-display text-2xl flex items-center gap-2 flex-wrap">
            {v.visit_number} <Badge variant="outline" className="uppercase text-[10px]">{v.visit_type.replace(/_/g, " ")}</Badge>
            <span className={chip(v.status)}>{v.status.replace(/_/g, " ")}</span>
          </DialogTitle>
          <DialogDescription>{v.customer_name}{v.truck_rego ? ` · ${v.truck_rego}` : ""}{v.driver ? ` · ${v.driver}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="hard-card p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <StageTracker v={v} />
          {!done && <div className="text-xs whitespace-nowrap"><span className="text-zinc-400 uppercase tracking-wider font-bold text-[10px]">Next</span> <span className="font-bold text-[#FF5F15]">{nextActionOf(v)}</span></div>}
        </div>

        {/* Weighbridge */}
        {!done && (
          <div className="hard-card p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-2 flex items-center gap-1.5"><Scale className="w-3.5 h-3.5" /> Weighbridge</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-end gap-2">
                <div className="flex-1"><Label className="text-[10px] uppercase font-bold">Gross (t)</Label><Input type="number" value={wb.inbound_gross || ""} onChange={(e) => setWb({ ...wb, inbound_gross: e.target.value })} data-testid="wb-gross" /></div>
                <Button size="sm" onClick={() => weigh("inbound_gross")} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-[11px] uppercase font-bold" data-testid="wb-gross-capture">Capture</Button>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1"><Label className="text-[10px] uppercase font-bold">Tare / empty (t)</Label><Input type="number" value={wb.inbound_tare || ""} onChange={(e) => setWb({ ...wb, inbound_tare: e.target.value })} data-testid="wb-tare" /></div>
                <Button size="sm" onClick={() => weigh("inbound_tare")} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-[11px] uppercase font-bold" data-testid="wb-tare-capture">Capture</Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              {weights.inbound_gross && <span>Gross: <b>{weights.inbound_gross.weight}t</b></span>}
              {v.latest_known_tare != null && !weights.inbound_tare && <span className="text-amber-600">Last known tare: <b>{v.latest_known_tare}t</b> · est. net <b>{estNet}t</b> <i className="text-[11px]">(estimated)</i></span>}
              {weights.inbound_tare && <span>Tare: <b>{weights.inbound_tare.weight}t</b></span>}
              {measuredNet && <span className="text-emerald-600 font-bold">Net: {measuredNet}t</span>}
            </div>
          </div>
        )}

        {/* Inbound confirm */}
        {isTip && !v.inbound_load_id && !done && (
          <div className="hard-card p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-2 flex items-center gap-1.5"><ArrowDownToLine className="w-3.5 h-3.5" /> Confirm inbound (tip)</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase font-bold">Material *</Label>
                <Select value={inb.material_id || ""} onValueChange={(x) => setInb({ ...inb, material_id: x })}>
                  <SelectTrigger data-testid="inb-material"><SelectValue placeholder="Material" /></SelectTrigger>
                  <SelectContent>{refs.materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[10px] uppercase font-bold">Destination stockpile *</Label>
                <Select value={inb.stockpile_id || ""} onValueChange={(x) => { const s = refs.stockpiles.find((y) => y.id === x); setInb({ ...inb, stockpile_id: x, unit: s ? s.unit : inb.unit }); }}>
                  <SelectTrigger data-testid="inb-stockpile"><SelectValue placeholder="Stockpile" /></SelectTrigger>
                  <SelectContent>{refs.stockpiles.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.contents_name} ({unitLabel(s.unit)})</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[10px] uppercase font-bold">Measurement</Label>
                <Select value={inb.measurement_source} onValueChange={(x) => setInb({ ...inb, measurement_source: x })}>
                  <SelectTrigger data-testid="inb-measure"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="WEIGHBRIDGE">Weighbridge (net)</SelectItem><SelectItem value="MANUAL">Manual quantity</SelectItem><SelectItem value="VOLUME">Volume (m³)</SelectItem><SelectItem value="LOAD_ESTIMATE">Truck load</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-[10px] uppercase font-bold">Unit</Label>
                <Select value={inb.unit} onValueChange={(x) => setInb({ ...inb, unit: x })}>
                  <SelectTrigger data-testid="inb-unit"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="t">Tonnes</SelectItem><SelectItem value="m3">m³</SelectItem><SelectItem value="load">Load</SelectItem></SelectContent>
                </Select></div>
              {inb.measurement_source !== "WEIGHBRIDGE" && (
                <div><Label className="text-[10px] uppercase font-bold">Quantity *</Label><Input type="number" value={inb.quantity || ""} onChange={(e) => setInb({ ...inb, quantity: e.target.value })} data-testid="inb-qty" /></div>
              )}
              <div><Label className="text-[10px] uppercase font-bold">Tipping charge</Label>
                <Select value={inb.charge_method} onValueChange={(x) => setInb({ ...inb, charge_method: x })}>
                  <SelectTrigger data-testid="inb-charge"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHARGE_METHODS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[10px] uppercase font-bold">Rate ($)</Label><Input type="number" value={inb.rate || ""} onChange={(e) => setInb({ ...inb, rate: e.target.value })} disabled={inb.charge_method === "FREE"} data-testid="inb-rate" /></div>
            </div>
            <Button onClick={confirmInbound} className="mt-3 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="inb-confirm">Confirm inbound</Button>
          </div>
        )}
        {v.inbound_summary && (
          <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold mb-1">Material received</div>
            {v.inbound_summary.material_name} — <b>{v.inbound_summary.quantity}{unitLabel(v.inbound_summary.unit)}</b> · Tipping {money2(v.inbound_summary.subtotal)} · {v.inbound_summary.docket_no}
            {v.inbound_load_id && <button onClick={() => openDocket(v.inbound_load_id)} className="ml-2 text-emerald-700 dark:text-emerald-400 font-bold underline underline-offset-2" data-testid="view-inbound-movement">View inbound movement</button>}
          </div>
        )}

        {/* Collect request */}
        {isCol && !v.outbound_load_id && v.status !== "LOADING" && !done && (
          <div className="hard-card p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-2 flex items-center gap-1.5"><ArrowUpFromLine className="w-3.5 h-3.5" /> Collection request → loader queue</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase font-bold">Product *</Label>
                <Select value={col.product_id || ""} onValueChange={(x) => { const p = refs.products.find((y) => y.id === x); setCol({ ...col, product_id: x, unit: p ? p.default_unit : col.unit }); }}>
                  <SelectTrigger data-testid="col-product"><SelectValue placeholder="Product" /></SelectTrigger>
                  <SelectContent>{refs.products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {money2(p.sell_price)}/{unitLabel(p.default_unit)} ({p.stock} avail)</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[10px] uppercase font-bold">Source stockpile *</Label>
                <Select value={col.stockpile_id || ""} onValueChange={(x) => setCol({ ...col, stockpile_id: x })}>
                  <SelectTrigger data-testid="col-stockpile"><SelectValue placeholder="Stockpile" /></SelectTrigger>
                  <SelectContent>{refs.stockpiles.filter((s) => s.product_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.contents_name} ({s.current_quantity}{unitLabel(s.unit)})</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[10px] uppercase font-bold">Target quantity *</Label><Input type="number" value={col.target_quantity || ""} onChange={(e) => setCol({ ...col, target_quantity: e.target.value })} data-testid="col-qty" /></div>
              <div><Label className="text-[10px] uppercase font-bold">Unit</Label>
                <Select value={col.unit} onValueChange={(x) => setCol({ ...col, unit: x })}>
                  <SelectTrigger data-testid="col-unit"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="t">Tonnes</SelectItem><SelectItem value="m3">m³</SelectItem><SelectItem value="load">Load</SelectItem></SelectContent>
                </Select></div>
            </div>
            <Button onClick={requestCollect} className="mt-3 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="col-request">Send to loader queue</Button>
          </div>
        )}
        {v.status === "WAITING_FOR_LOADER" && !v.outbound_load_id && (
          <div className="rounded-lg bg-purple-500/10 border border-purple-500/20 p-3 text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-purple-600" /> Waiting for the loader to complete this collection.</div>
        )}
        {v.status === "LOADING" && <div className="rounded-lg bg-orange-500/10 border border-orange-500/20 p-3 text-sm">Loading in progress…</div>}
        {v.outbound_summary && (
          <div className="rounded-lg bg-[#FF5F15]/5 border border-[#FF5F15]/20 p-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-[#FF5F15] font-bold mb-1">Material supplied</div>
            {v.outbound_summary.product_name} — <b>{v.outbound_summary.quantity}{unitLabel(v.outbound_summary.unit)}</b>{v.outbound_summary.weighbridge_net != null ? ` (loader est. ${v.outbound_summary.loader_estimate})` : ""} · {money2(v.outbound_summary.subtotal)} · {v.outbound_summary.docket_no}
            {v.outbound_load_id && <button onClick={() => openDocket(v.outbound_load_id)} className="ml-2 text-[#FF5F15] font-bold underline underline-offset-2" data-testid="view-outbound-movement">View outbound movement</button>}
          </div>
        )}

        {/* Timeline */}
        <div className="hard-card p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF5F15] font-bold mb-2">Timeline</div>
          <div className="space-y-1 text-sm">{(v.timeline || []).map((t, i) => (
            <div key={i} className="flex gap-3"><span className="font-mono text-zinc-400 w-12 shrink-0">{t.time}</span><span className="font-bold">{t.event}</span><span className="text-zinc-500 truncate">{t.detail}</span></div>
          ))}</div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {v.visit_total != null && <div className="mr-auto text-sm font-bold">Visit total: <span className="text-[#FF5F15]">{money2(v.visit_total)}</span></div>}
          <Button variant="outline" onClick={openPdf} className="rounded-sm uppercase text-xs font-bold tracking-wider" data-testid="visit-pdf"><FileDown className="w-4 h-4 mr-1.5" /> Docket</Button>
          {!done && <Button onClick={complete} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 uppercase text-xs font-bold tracking-wider" data-testid="visit-complete"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Complete visit</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CountTile = ({ label, value, testId, active, onClick }) => (
  <button onClick={onClick} className={`hard-card p-3 text-center transition-colors ${active ? "ring-2 ring-[#FF5F15]" : "hover:border-[#FF5F15]/40"}`} data-testid={testId}>
    <div className="text-2xl font-display font-extrabold text-zinc-900 dark:text-white">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold leading-tight mt-0.5">{label}</div>
  </button>
);

const minsSince = (iso) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
};
const waitLabel = (m) => (m == null ? "" : m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`);

function nextActionOf(v) {
  const w = v.weights || {};
  const isTip = v.visit_type !== "COLLECT";
  switch (v.status) {
    case "COMPLETED": return "Completed";
    case "READY_TO_LEAVE": return "Complete visit";
    case "LOADING": return "Loading in progress";
    case "WAITING_FOR_LOADER": return v.outbound_load_id ? "Complete visit" : "Send to loader / loading pending";
    case "TIPPING": return "Capture tare after tipping";
    case "WAITING_TO_TIP": return isTip && !v.inbound_load_id ? (w.inbound_tare ? "Confirm inbound" : "Tip, then capture tare") : "Confirm inbound";
    case "ARRIVED": return isTip ? "Capture gross weight" : "Weigh empty or send to loader";
    default: return "View visit";
  }
}

function stagesOf(v) {
  const w = v.weights || {};
  const isTip = v.visit_type !== "COLLECT";
  const isCol = v.visit_type !== "TIP";
  const s = [{ k: "ARRIVED", done: true }];
  if (isTip) {
    s.push({ k: "WEIGH IN", done: !!w.inbound_gross });
    s.push({ k: "TIP", done: !!v.inbound_load_id });
    s.push({ k: "EMPTY WEIGH", done: !!w.inbound_tare });
  }
  if (isCol) {
    s.push({ k: "LOADER", done: !!v.outbound_load_id });
    s.push({ k: "FINAL WEIGH", done: !!(v.outbound_summary && v.outbound_summary.weighbridge_net != null) });
  }
  s.push({ k: "DOCKET", done: v.status === "READY_TO_LEAVE" || v.status === "COMPLETED" });
  s.push({ k: "OUT", done: v.status === "COMPLETED" });
  const firstOpen = s.findIndex((x) => !x.done);
  if (firstOpen >= 0) s[firstOpen].active = true;
  return s;
}

const TypeTag = ({ t }) => (
  t === "TIP" ? <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-xs"><ArrowDownToLine className="w-3.5 h-3.5" /> TIP</span>
    : t === "COLLECT" ? <span className="inline-flex items-center gap-1 text-[#FF5F15] font-bold text-xs"><ArrowUpFromLine className="w-3.5 h-3.5" /> COLLECT</span>
      : <span className="inline-flex items-center gap-1 text-purple-600 font-bold text-xs"><ArrowDownToLine className="w-3.5 h-3.5" /> TIP <span className="text-zinc-400">+</span> <ArrowUpFromLine className="w-3.5 h-3.5" /> COLLECT</span>
);

function StageTracker({ v, compact }) {
  const steps = stagesOf(v);
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {steps.map((s, i) => (
        <div key={s.k} className="flex items-center gap-1">
          <div className="flex flex-col items-center">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${s.done ? "bg-emerald-500 text-white" : s.active ? "bg-[#FF5F15] text-white animate-pulse" : "bg-zinc-200 dark:bg-white/10 text-zinc-400"}`}>
              {s.done ? "✓" : s.active ? "●" : ""}
            </span>
            {!compact && <span className={`text-[8px] uppercase mt-0.5 tracking-wide ${s.active ? "text-[#FF5F15] font-bold" : "text-zinc-400"}`}>{s.k}</span>}
          </div>
          {i < steps.length - 1 && <span className={`w-3 h-px ${s.done ? "bg-emerald-400" : "bg-zinc-200 dark:bg-white/10"}`} />}
        </div>
      ))}
    </div>
  );
}

function LiveVisitCard({ v, onOpen }) {
  const inYard = waitLabel(minsSince(v.arrival_time));
  const stageWait = waitLabel(minsSince(v.updated_at));
  const isTip = v.visit_type !== "COLLECT";
  const isCol = v.visit_type !== "TIP";
  return (
    <button onClick={onOpen} className="hard-card p-4 text-left w-full hover:border-[#FF5F15]/50 transition-colors" data-testid={`visit-card-${v.visit_number}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-display font-extrabold">{v.truck_rego || v.visit_number}</span>
            <span className={chip(v.status)}>{v.status.replace(/_/g, " ")}</span>
          </div>
          <div className="text-sm text-zinc-500 truncate">{v.customer_name}{v.truck_type ? ` · ${v.truck_type}` : ""}</div>
          <div className="mt-1"><TypeTag t={v.visit_type} /></div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[11px] text-zinc-400">In yard</div>
          <div className="font-bold flex items-center gap-1 justify-end"><Clock className="w-3.5 h-3.5 text-zinc-400" />{inYard}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 my-2">
        <div className={`rounded-lg p-2 ${isTip ? "bg-emerald-500/5 border border-emerald-500/15" : "bg-zinc-100/50 dark:bg-white/[0.02] border border-transparent"}`}>
          <div className="text-[9px] uppercase tracking-wider text-emerald-600 font-bold flex items-center gap-1"><ArrowDownToLine className="w-3 h-3" /> Inbound</div>
          {v.inbound_summary ? <div className="text-sm"><b>{v.inbound_summary.quantity}{unitLabel(v.inbound_summary.unit)}</b> {v.inbound_summary.material_name} <span className="text-emerald-600 text-[11px]">✓</span></div>
            : isTip ? <div className="text-xs text-zinc-400">Awaiting tip</div> : <div className="text-xs text-zinc-300 dark:text-zinc-600">—</div>}
        </div>
        <div className={`rounded-lg p-2 ${isCol ? "bg-[#FF5F15]/5 border border-[#FF5F15]/15" : "bg-zinc-100/50 dark:bg-white/[0.02] border border-transparent"}`}>
          <div className="text-[9px] uppercase tracking-wider text-[#FF5F15] font-bold flex items-center gap-1"><ArrowUpFromLine className="w-3 h-3" /> Outbound</div>
          {v.outbound_summary ? <div className="text-sm"><b>{v.outbound_summary.quantity}{unitLabel(v.outbound_summary.unit)}</b> {v.outbound_summary.product_name} <span className="text-[#FF5F15] text-[11px]">✓</span></div>
            : isCol ? <div className="text-xs text-zinc-400">{v.status === "LOADING" ? "Loading…" : v.status === "WAITING_FOR_LOADER" ? "Waiting for loader" : "To collect"}</div> : <div className="text-xs text-zinc-300 dark:text-zinc-600">—</div>}
        </div>
      </div>

      <div className="py-2 border-t border-zinc-100 dark:border-white/5"><StageTracker v={v} /></div>

      <div className="flex items-center justify-between mt-1">
        <div className="text-xs"><span className="text-zinc-400 uppercase tracking-wider font-bold text-[10px]">Next</span> <span className="font-bold text-[#FF5F15]">{nextActionOf(v)}</span></div>
        <span className="text-xs text-zinc-500 flex items-center gap-1">View <ChevronRight className="w-3.5 h-3.5" /></span>
      </div>
    </button>
  );
}

export function SupplyYardVisits() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ visits: [], counts: {} });
  const [scope, setScope] = useState("active");
  const [stageFilter, setStageFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [initialType, setInitialType] = useState("");
  const [detailId, setDetailId] = useState(null);
  const load = useCallback(() => { api.get(`/sy/visits?scope=${scope}`).then((r) => setData(r.data)).catch(() => {}); }, [scope]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const n = searchParams.get("new");
    if (n) { setInitialType(n === "1" ? "" : n); setNewOpen(true); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);
  const c = data.counts || {};
  const visits = (data.visits || []).filter((v) => {
    if (stageFilter && v.status !== stageFilter) return false;
    if (typeFilter && v.visit_type !== typeFilter) return false;
    if (q) {
      const s = `${v.visit_number} ${v.truck_rego || ""} ${v.customer_name || ""}`.toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const showCards = scope === "active";
  const tiles = [["", "In yard", c.IN_YARD || 0], ["WAITING_TO_TIP", "Waiting to tip", c.WAITING_TO_TIP || 0],
    ["TIPPING", "Tipping", c.TIPPING || 0], ["WAITING_FOR_LOADER", "Waiting loader", c.WAITING_FOR_LOADER || 0],
    ["LOADING", "Loading", c.LOADING || 0], ["READY_TO_LEAVE", "Ready to leave", c.READY_TO_LEAVE || 0]];

  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 md:max-w-[1600px] md:mx-auto pb-24 md:pb-10" data-testid="sy-visits-page">
      <PageHeader tag="supply yard / yard visits" title="Yard Visits" subtitle="What is happening in the yard right now — tip, collect or both."
        action={<Button onClick={() => setNewOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider" data-testid="new-yard-visit"><Plus className="w-4 h-4 mr-1.5" /> New yard visit</Button>} />

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-5">
        {tiles.map(([st, label, val]) => (
          <CountTile key={label} label={label} value={val} active={showCards && stageFilter === st}
            onClick={() => { setScope("active"); setStageFilter(stageFilter === st ? "" : st); }}
            testId={`count-${label.toLowerCase().replace(/ /g, "-")}`} />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex gap-1.5">{[["active", "In yard"], ["today", "Today"], ["", "All"]].map(([s, l]) => (
          <button key={s} onClick={() => { setScope(s); setStageFilter(""); }} className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold border ${scope === s ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300"}`} data-testid={`visits-scope-${s || "all"}`}>{l}</button>
        ))}</div>
        <div className="relative flex-1 max-w-xs"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search truck, customer, visit…" className="pl-9 h-8" data-testid="visits-search" /></div>
        <div className="flex gap-1.5">{[["", "All types"], ["TIP", "Tip"], ["COLLECT", "Collect"], ["TIP_AND_COLLECT", "Tip+Collect"]].map(([t, l]) => (
          <button key={t || "all"} onClick={() => setTypeFilter(typeFilter === t ? "" : t)} className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider font-bold border ${typeFilter === t ? "bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 border-transparent" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300"}`} data-testid={`visits-type-${t || "all"}`}>{l}</button>
        ))}</div>
      </div>

      {visits.length === 0 ? (
        <EmptyState icon={Truck} title={showCards ? "No trucks in the yard" : "No visits"} description={showCards ? "Start a new yard visit when a truck arrives." : "No visits match this view."}
          action={<Button onClick={() => setNewOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-xs font-bold tracking-wider">New yard visit</Button>} />
      ) : showCards ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{visits.map((v) => <LiveVisitCard key={v.id} v={v} onOpen={() => nav(`/app/supply-yard/visits/${v.id}`)} />)}</div>
      ) : (
        <div className="hard-card overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-400 text-left border-b border-zinc-100 dark:border-white/10"><th className="p-3">Visit</th><th>Arrived</th><th>Duration</th><th>Truck</th><th>Customer</th><th>Type</th><th>Inbound</th><th>Outbound</th><th className="text-right">Total</th><th className="text-right">Status</th></tr></thead>
          <tbody>{visits.map((v) => {
            const dur = v.arrival_time && v.departure_time ? waitLabel(Math.round((Date.parse(v.departure_time) - Date.parse(v.arrival_time)) / 60000)) : "—";
            return (
              <tr key={v.id} className="border-b border-zinc-50 dark:border-white/5 cursor-pointer hover:bg-zinc-50 dark:hover:bg-white/[0.03]" onClick={() => nav(`/app/supply-yard/visits/${v.id}`)} data-testid={`visit-row-${v.visit_number}`}>
                <td className="p-3 font-bold">{v.visit_number}</td>
                <td className="text-zinc-500">{(v.arrival_time || "").slice(11, 16)}</td>
                <td className="text-zinc-500">{dur}</td>
                <td className="font-mono">{v.truck_rego || "—"}</td>
                <td>{v.customer_name}</td>
                <td><TypeTag t={v.visit_type} /></td>
                <td className="text-zinc-500">{v.inbound_summary ? `${v.inbound_summary.quantity}${unitLabel(v.inbound_summary.unit)}` : "—"}</td>
                <td className="text-zinc-500">{v.outbound_summary ? `${v.outbound_summary.quantity}${unitLabel(v.outbound_summary.unit)}` : "—"}</td>
                <td className="text-right font-mono">{v.visit_total != null ? money2(v.visit_total) : "—"}</td>
                <td className="text-right"><span className={chip(v.status)}>{v.status.replace(/_/g, " ")}</span></td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
      <NewVisitDialog open={newOpen} onOpenChange={(v) => { setNewOpen(v); if (!v) setInitialType(""); }} initialType={initialType} onCreated={(v) => nav(`/app/supply-yard/visits/${v.id}`)} />
      <VisitDetail visitId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onChanged={load} />
    </div>
  );
}
